import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core'

import { TranslatePipe } from '@ngx-translate/core'
import { PrimeTemplate } from 'primeng/api'
import { Button } from 'primeng/button'
import { Dialog } from 'primeng/dialog'
import { IconField } from 'primeng/iconfield'
import { InputIcon } from 'primeng/inputicon'
import { InputText } from 'primeng/inputtext'

import { WhiteLabelSettingsProvider } from '@ti/core/common/services'
import { ClipboardProvider } from '@ti/core/contracts/clipboard'
import { AlertCircleIconComponent } from '@ti/core/icons/alert-circle'
import { CopyOutlined1IconComponent } from '@ti/core/icons/copy-outlined-1'

import { MfaClient } from '../../services/mfa.client'
import { AuthenticatorCodeDialogComponent } from '../authenticator-code-dialog/authenticator-code-dialog.component'

@Component({
  selector: 'app-authenticator-setup-dialog',
  templateUrl: 'authenticator-setup-dialog.component.html',
  styleUrls: ['authenticator-setup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertCircleIconComponent,
    AuthenticatorCodeDialogComponent,
    Button,
    CopyOutlined1IconComponent,
    Dialog,
    IconField,
    InputIcon,
    InputText,
    PrimeTemplate,
    TranslatePipe,
  ],
})
export class AuthenticatorSetupDialogComponent {
  protected readonly mfaClient = inject(MfaClient)
  protected readonly whiteLabelSettings = inject(WhiteLabelSettingsProvider)
  protected readonly clipboard = inject(ClipboardProvider)

  @ViewChild('qrCanvas')
  protected readonly qrCanvas?: ElementRef<HTMLCanvasElement>

  public readonly isOpened = model(false)
  public readonly cancelDisabled = model(false)

  protected readonly currentMfaUri = signal<URL | undefined>(undefined)
  protected readonly currentMfaSecret = signal<string | null | undefined>(undefined)

  public open() {
    this.isOpened.set(true)
    this.initMFASetupSession().catch((e) => console.log(`Cannot init MFA setup session`, e))
  }

  public close() {
    this.isOpened.set(false)
  }

  protected async copySetupKey() {
    const secret = this.currentMfaSecret()
    if (secret) {
      await this.clipboard.writeText(secret)
    }
  }

  protected async initMFASetupSession() {
    if (!this.qrCanvas) return
    const whiteLabelSettings = await this.whiteLabelSettings.getData()

    const { sharedSecret, setupUri } = await this.mfaClient.getMfaSetupData(
      whiteLabelSettings?.name || 'TelematicsInsights',
    )
    this.currentMfaUri.set(setupUri)
    this.currentMfaSecret.set(sharedSecret)
    const { toCanvas } = await import('qrcode')
    await toCanvas(this.qrCanvas.nativeElement, setupUri.toString(), {
      margin: 1,
      width: 256,
    })
  }
}
